import {useEffect, useState} from 'react';
import cx from 'classnames';
import NextLink from 'next/link';
import {useRouter} from 'next/router';

import {Avatar, Text} from '#component/base';
import RectangleSkeleton from '#component/base/skeletons/RectangleSkeleton';
import {FullscreenLoading} from '#component/partial';
import {HeaderDropdown, MenuItem} from '#component/partial/Header/components';
import {useViewportSize} from '#hook';
import {getSignOutUrl} from '#util';

import {useProfileHeader} from './useProfileHeader';

import {
  activeLinkMenu,
  avatarCustom,
  headerContainer,
  headerContainerBackground,
  headingUserName,
  innerContent,
  menuHeader,
  menuItem,
  menuItemText,
  mobileMenuItem,
  triggerHeaderMobile,
  userEmail,
  userInfoContainer,
  userInfoDataWrapper,
} from './ProfileHeader.css';

const menuItems = [
  {
    href: '/profile' as const,
    label: 'My Account',
  },
  {
    href: '/profile?tab=manage' as const,
    label: 'Manage My Plan',
  },
  {
    href: '/profile?tab=profile' as const,
    label: 'Profile Info',
  },
  {
    href: '/profile?tab=notifications' as const,
    label: 'Notifications',
  },
  {
    href: '/profile?tab=privacy' as const,
    label: 'Privacy',
  },
  {
    href: getSignOutUrl(),
    label: 'Logout',
  },
];
const ProfileHeader = () => {
  const {data, loading} = useProfileHeader();
  const {isMobile} = useViewportSize();
  const {avatar, userProfile, subscription, mostRecentLabSubmission} = data?.me || {};
  const {firstName, initials, lastName, email} = userProfile || {};
  const router = useRouter();
  const [openMobile, setOpenMobile] = useState(false);
  const tab = (router.query.tab as string) || 'account';

  useEffect(() => {
    setOpenMobile(false);
  }, [tab]);

  const handleOpenLinkMobile = () => {
    setOpenMobile(!openMobile);
  };

  const filteredMenuItems = menuItems.filter(item => {
    if (item.href === '/profile?tab=manage' && !subscription && !mostRecentLabSubmission) {
      return false;
    }
    return true;
  });

  const currentMenuItem =
    filteredMenuItems.find(item => item.label.toLocaleLowerCase().includes(tab)) ||
    filteredMenuItems[0];
  const mobileMenuItems = filteredMenuItems.filter(
    item => !item.label.toLocaleLowerCase().includes(tab),
  );

  if (loading) {
    return <FullscreenLoading />;
  }

  return !loading ? (
    <div className={headerContainer}>
      <div className={headerContainerBackground} />
      <div className={innerContent}>
        <div className={userInfoContainer}>
          <Avatar
            image={avatar?.url || null}
            text={initials || '??'}
            size={isMobile ? 'small' : 'medium'}
            color="grapefruit"
            className={avatarCustom}
            textSize={isMobile ? 'smaller' : 'small'}
          />
          <div className={userInfoDataWrapper}>
            <Text className={headingUserName} color="burgundy" variant="heading2">
              <span>{`${firstName} ${lastName}`}</span>
            </Text>
            <Text className={userEmail} color="dustyGray" variant="caption">
              <span>{email}</span>
            </Text>
          </div>
        </div>
        {isMobile ? (
          <HeaderDropdown
            open={openMobile}
            trigger={currentMenuItem.label}
            onOpenChange={handleOpenLinkMobile}
            className={triggerHeaderMobile}>
            {mobileMenuItems.map((item, index) => (
              <MenuItem key={index} href={item.href} className={mobileMenuItem}>
                <Text
                  color={index === mobileMenuItems.length - 1 ? 'dustyGray' : 'burgundy'}
                  variant="button">
                  {item.label}
                </Text>
              </MenuItem>
            ))}
          </HeaderDropdown>
        ) : (
          <ul className={menuHeader}>
            {filteredMenuItems.map((item, index) => (
              <li
                className={cx(menuItem, item.label.toLowerCase().includes(tab) && activeLinkMenu)}
                key={index}>
                <NextLink href={item.href}>
                  <Text color="white" variant="button" className={menuItemText}>
                    {item.label}
                  </Text>
                </NextLink>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  ) : (
    <RectangleSkeleton height={400} width={1200} />
  );
};

export default ProfileHeader;
